// Shared status vocabulary + badges for the admin logbook screens.
// Tabs use only statuses the DB actually persists (the `status` enum:
// in_progress, submitted, graded, repeating, completed). The badge itself
// renders any string gracefully, so legacy values (accepted/awaiting_assessment)
// still show if they ever appear.

export type LbStatusKey =
  | "all"
  | "in_progress"
  | "submitted"
  | "awaiting_assessment"
  | "graded"
  | "repeating"
  | "completed";

export const LB_STATUS_TABS: { key: LbStatusKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "in_progress", label: "In Progress" },
  { key: "submitted", label: "Submitted" },
  { key: "awaiting_assessment", label: "Awaiting" },
  { key: "graded", label: "Graded" },
  { key: "repeating", label: "Repeating" },
  { key: "completed", label: "Completed" },
];

// Collapse raw statuses into the tab bucket they belong to.
// 'accepted' is a pre-submission editable state → shown as In Progress.
export function lbBucket(status?: string): string {
  const s = String(status ?? "").toLowerCase();
  if (s === "accepted") return "in_progress";
  return s;
}

export function lbStatusColors(key: string) {
  switch (key) {
    case "completed":
      return { border: "border-green-200", active: "bg-green-600 text-white border-green-600", inactive: "bg-green-50 text-green-800 hover:bg-green-100", pill: "bg-green-100 text-green-800 border-green-200" };
    case "graded":
      return { border: "border-indigo-200", active: "bg-indigo-600 text-white border-indigo-600", inactive: "bg-indigo-50 text-indigo-800 hover:bg-indigo-100", pill: "bg-indigo-100 text-indigo-800 border-indigo-200" };
    case "submitted":
      return { border: "border-sky-200", active: "bg-sky-600 text-white border-sky-600", inactive: "bg-sky-50 text-sky-800 hover:bg-sky-100", pill: "bg-sky-100 text-sky-800 border-sky-200" };
    case "awaiting_assessment":
      return { border: "border-teal-200", active: "bg-teal-600 text-white border-teal-600", inactive: "bg-teal-50 text-teal-800 hover:bg-teal-100", pill: "bg-teal-100 text-teal-800 border-teal-200" };
    case "in_progress":
    case "accepted":
      return { border: "border-blue-200", active: "bg-blue-600 text-white border-blue-600", inactive: "bg-blue-50 text-blue-800 hover:bg-blue-100", pill: "bg-blue-100 text-blue-800 border-blue-200" };
    case "repeating":
      return { border: "border-orange-200", active: "bg-orange-600 text-white border-orange-600", inactive: "bg-orange-50 text-orange-900 hover:bg-orange-100", pill: "bg-orange-100 text-orange-900 border-orange-200" };
    case "rejected":
      return { border: "border-red-200", active: "bg-red-600 text-white border-red-600", inactive: "bg-red-50 text-red-800 hover:bg-red-100", pill: "bg-red-100 text-red-800 border-red-200" };
    default:
      return { border: "border-gray-300", active: "bg-gray-700 text-white border-gray-700", inactive: "bg-gray-50 text-gray-700 hover:bg-gray-100", pill: "bg-gray-100 text-gray-700 border-gray-300" };
  }
}

export function LbStatusBadge({ status }: { status?: string }) {
  const s = String(status ?? "in_progress").toLowerCase();
  const c = lbStatusColors(s);
  return (
    <span className={`inline-flex items-center px-2 py-1 text-xs border rounded-full ${c.pill}`} title={s}>
      {s.replace(/_/g, " ")}
    </span>
  );
}

export function LbCountPill({ tone, count }: { tone: string; count: number }) {
  const c = lbStatusColors(tone);
  return <span className={`ml-2 inline-flex items-center px-2 py-0.5 text-xs border rounded-full ${c.pill}`}>{count}</span>;
}

export function fmtDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? String(iso)
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// Member display name from a supervisor/assessor Member record.
export function memberName(m?: any): string {
  if (!m) return "—";
  return m.full_name || [m.surname, m.othernames, m.firstname].filter(Boolean).join(" ") || m.membership_no || "—";
}
